import React, { useState } from 'react';
import { staffPhone } from '../../lib/staffPhone';
import { useSaveToast } from '../ui/SaveToast';
import { MOBILE_EXAMPLE_MESSAGE } from '../../lib/staffPhone';
import PhoneInput from '../ui/PhoneInput';
import { formatPhone } from '../../lib/phone';

// BL-177 Step 2c (A7): the one staff mobile that text alerts use (personnel_people.mobile) can be changed by an admin
// right on the person's card in Staff & Roles. Stored as +14045551234, shown as +1 (404) 555-1234 (BL-177 Step 2d).
export default function StaffMobileEditor({ member, currentPhone, canEdit = false, client = null, onSaved }) {
    const toast = useSaveToast();
    const [saved, setSaved] = useState(currentPhone || '');
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    async function save(event) {
        event.preventDefault();
        if (busy) return;
        const phone = staffPhone(draft.trim(), 'US');
        if (!phone.e164) {
            setError(MOBILE_EXAMPLE_MESSAGE);
            return;
        }
        setBusy(true);
        setError('');
        try {
            // Loaded only when a save happens, so showing a staff card never needs the database client.
            const db = client || (await import('../../config/supabase')).supabaseClient;
            const { data, error: rpcError } = await db.rpc('bl177_set_staff_mobile', { p_staff_id: member.id, p_mobile: phone.e164 });
            if (rpcError) throw rpcError;
            const stored = typeof data === 'string' && data ? data : phone.e164;
            setSaved(stored);
            setEditing(false);
            onSaved?.(stored);
            toast.saved();
        } catch (failure) {
            const message = failure?.message || 'The mobile number could not be saved.';
            setError(message);
            toast.failed(message);
        } finally {
            setBusy(false);
        }
    }

    if (!editing) {
        return (
            <div className="space-y-1">
                <div className="text-[11px] uppercase tracking-wide text-[var(--ink-muted)]">Mobile</div>
                <div className="flex flex-wrap items-center gap-2 text-sm text-[var(--ink)]">
                    <span>{saved ? formatPhone(saved) : '—'}</span>
                    {canEdit && (
                        <button type="button" className="text-xs underline" aria-label={`Edit mobile number for text alerts`}
                            onClick={() => { setDraft(saved || ''); setError(''); setEditing(true); }}>
                            {saved ? 'Edit mobile' : 'Add mobile'}
                        </button>
                    )}
                </div>
                {!saved && <p className="text-xs text-[var(--ink-muted)]">No mobile number: this person will not get text alerts.</p>}
            </div>
        );
    }
    return (
        <form onSubmit={save} className="space-y-1">
            <label className="block text-[11px] uppercase tracking-wide text-[var(--ink-muted)]" htmlFor={`staff-mobile-${member.id}`}>Mobile for text alerts</label>
            <PhoneInput id={`staff-mobile-${member.id}`} value={draft} placeholder="+1 (404) 555-1234" disabled={busy}
                onChange={(value) => setDraft(value)} />
            {error && <p role="alert" className="text-xs text-red-700">{error}</p>}
            <div className="flex gap-2">
                <button type="submit" disabled={busy} className="rounded border px-2 py-1 text-xs">{busy ? 'Saving…' : 'Save'}</button>
                <button type="button" disabled={busy} className="rounded border px-2 py-1 text-xs" onClick={() => setEditing(false)}>Cancel</button>
            </div>
        </form>
    );
}
